import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./../../../assets/css/student/CodeChallenges.css";
import MyStudentLayout from "../../../layouts/Student/MyStudentLayout";

const leaderboard = [
  {
    rank: 1,
    name: "NeuralNinja",
    score: 4820,
    avatar: "NN",
    color: "#B8860B",
    glow: "#FFD700",
  },
  {
    rank: 2,
    name: "ByteSlayer",
    score: 4510,
    avatar: "BS",
    color: "#555",
    glow: "#C0C0C0",
  },
  {
    rank: 3,
    name: "CodeViper",
    score: 4230,
    avatar: "CV",
    color: "#7A4520",
    glow: "#CD7F32",
  },
  {
    rank: 42,
    name: "You",
    score: 2180,
    avatar: "YO",
    color: "#7A1C1C",
    glow: "#FF6B6B",
    isUser: true,
  },
];

const recentMatches = [
  {
    result: "WIN",
    opponent: "AlgoKing",
    lang: "Python",
    xp: "+80 XP",
    win: true,
  },
  {
    result: "LOSS",
    opponent: "NeuralNinja",
    lang: "JavaScript",
    xp: "-20 XP",
    win: false,
  },
  {
    result: "WIN",
    opponent: "ByteSlayer",
    lang: "C++",
    xp: "+95 XP",
    win: true,
  },
];

const achievements = [
  {
    icon: "🏹",
    title: "Sharpshooter",
    desc: "5 wins in a row",
    rarity: "RARE",
    color: "#A855F7",
  },
  {
    icon: "⚡",
    title: "Speed Coder",
    desc: "Solved in under 2 min",
    rarity: "EPIC",
    color: "#FFD166",
  },
  {
    icon: "🛡️",
    title: "Iron Defense",
    desc: "Zero bugs, 3 matches",
    rarity: "COMMON",
    color: "#00D4FF",
  },
];

const NUDGE = {
  message:
    "You lost your last match. Try Practice Mode to sharpen your skills before the next battle.",
};

export default function CodeArena() {
  const [activeAction, setActiveAction] = useState(null);
  const [pulseReady, setPulseReady] = useState(false);
  const [streakAnim, setStreakAnim] = useState(false);
  const [winbarWidth, setWinbarWidth] = useState(0);
  const [xpbarWidth, setXpbarWidth] = useState(0);

  useEffect(() => {
    const t1 = setTimeout(() => setPulseReady(true), 600);
    const t2 = setTimeout(() => setStreakAnim(true), 900);
    const t3 = setTimeout(() => setWinbarWidth(60), 400);
    const t4 = setTimeout(() => setXpbarWidth(68), 500);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, []);

  const handleAction = (action) => {
    setActiveAction(action);
    setTimeout(() => setActiveAction(null), 400);
  };
  const navigate = useNavigate();
  return (
    <MyStudentLayout>
      <div className="ca-page">
        {/* ── Background layers ── */}
        <div className="ca-page-bg" />
        <div className="ca-page-glow ca-pg1" />
        <div className="ca-page-glow ca-pg2" />
        <div className="ca-page-glow ca-pg3" />

        {/* ── Page Header ── */}
        <div className="ca-page-header">
          <div className="ca-page-header-left">
            <div className="ca-page-title-row">
              <span className="ca-sword ca-sword--left">⚔️</span>
              <h1 className="ca-page-title">Code Arena</h1>
              <span className="ca-sword ca-sword--right">⚔️</span>
            </div>
            <p className="ca-page-tagline">
              Practice&nbsp;·&nbsp;Compete&nbsp;·&nbsp;Improve
            </p>
          </div>
          <div className="ca-page-header-right">
            <div className="ca-badge-live">
              <span className="ca-live-dot" />
              LIVE
            </div>
            <div className="ca-players-online">
              <span className="ca-players-count">247</span>
              <span className="ca-players-label">fighters online</span>
            </div>
          </div>
        </div>

        {/* ── AI Nudge ── */}
        <div className="ca-nudge">
          <span className="ca-nudge-icon">⚠️</span>
          <p className="ca-nudge-text">{NUDGE.message}</p>
        </div>

        {/* ── Two-column grid ── */}
        <div className="ca-grid">
          {/* ══════ LEFT COLUMN ══════ */}
          <div className="ca-col-left">
            {/* Player Stats */}
            <div className="ca-card">
              <div className="ca-card-label">⚔️ Your Stats</div>
              <div className="ca-stats-row">
                <div className="ca-stat ca-stat--rank">
                  <div className="ca-stat-icon">🏆</div>
                  <div className="ca-stat-value ca-rank-gradient">#42</div>
                  <div className="ca-stat-label">Global Rank</div>
                </div>

                <div className="ca-stat-divider" />

                <div className="ca-stat">
                  <div className="ca-stat-icon">🎯</div>
                  <div className="ca-stat-value ca-win-gradient">
                    60<span className="ca-pct">%</span>
                  </div>
                  <div className="ca-stat-label">Win Rate</div>
                  <div className="ca-winbar-track">
                    <div
                      className="ca-winbar-fill"
                      style={{ width: `${winbarWidth}%` }}
                    />
                  </div>
                </div>

                <div className="ca-stat-divider" />

                <div
                  className={`ca-stat ${streakAnim ? "ca-stat--streak-anim" : ""}`}
                >
                  <div className="ca-stat-icon">
                    <span className="ca-fire">🔥</span>
                  </div>
                  <div className="ca-stat-value ca-streak-gradient">5</div>
                  <div className="ca-stat-label">Win Streak</div>
                </div>

                <div className="ca-stat-divider" />

                <div className="ca-stat">
                  <div className="ca-stat-icon">⚡</div>
                  <div className="ca-stat-value ca-xp-gradient">2,180</div>
                  <div className="ca-stat-label">Total XP</div>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="ca-card">
              <div className="ca-card-label">🚀 Quick Actions</div>
              <div className="ca-actions">
                <button
                  className={`ca-action ca-action--battle ${activeAction === "battle" ? "ca-action--pressed" : ""}`}
                  onClick={() => handleAction("battle")}
                >
                  <span className="ca-action-icon">⚔️</span>
                  <div className="ca-action-text">
                    <span className="ca-action-label">Battle Now</span>
                    <span className="ca-action-sub">Ranked Match</span>
                  </div>
                  <span className="ca-action-arrow">→</span>
                  <div className="ca-action-shine" />
                </button>

                <button
                  className={`ca-action ca-action--practice ${activeAction === "practice" ? "ca-action--pressed" : ""}`}
                  onClick={() => handleAction("practice")}
                >
                  <span className="ca-action-icon">🧠</span>
                  <div className="ca-action-text">
                    <span className="ca-action-label">Practice Mode</span>
                    <span className="ca-action-sub">Safe Zone · No Risk</span>
                  </div>
                  <span className="ca-action-arrow">→</span>
                  <div className="ca-action-shine" />
                </button>

                <button
                  className={`ca-action ca-action--daily ${activeAction === "daily" ? "ca-action--pressed" : ""}`}
                  onClick={() => handleAction("daily")}
                >
                  <span className="ca-action-icon">🔥</span>
                  <div className="ca-action-text">
                    <span className="ca-action-label">Daily Challenge</span>
                    <span className="ca-action-sub">+2× XP Today</span>
                  </div>
                  <div className="ca-action-badge">HOT</div>
                  <div className="ca-action-shine" />
                </button>
              </div>
            </div>

            {/* Recent Matches */}
            <div className="ca-card">
              <div className="ca-card-label">📋 Recent Matches</div>
              <div className="ca-matches-list">
                {recentMatches.map((m, i) => (
                  <div className="ca-match-row" key={i}>
                    <div
                      className={`ca-match-result ${m.win ? "ca-match--win" : "ca-match--loss"}`}
                    >
                      {m.result}
                    </div>
                    <span className="ca-match-vs">vs</span>
                    <span className="ca-match-opponent">{m.opponent}</span>
                    <span className="ca-match-lang">{m.lang}</span>
                    <span
                      className={`ca-match-xp ${m.win ? "ca-match--win" : "ca-match--loss"}`}
                    >
                      {m.xp}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ══════ RIGHT COLUMN ══════ */}
          <div className="ca-col-right">
            {/* Leaderboard */}
            <div className="ca-card">
              <div className="ca-lb-header">
                <span className="ca-card-label">🏅 Leaderboard</span>
                <span className="ca-lb-meta">Top Fighters</span>
              </div>
              <div className="ca-lb-list">
                {leaderboard.map((p) => (
                  <div
                    key={p.rank}
                    className={`ca-lb-row
                    ${p.isUser ? "ca-lb-row--you" : ""}
                    ${p.rank <= 3 ? `ca-lb-row--top${p.rank}` : ""}`}
                  >
                    <div className="ca-lb-rank">
                      {p.rank === 1
                        ? "👑"
                        : p.rank === 2
                          ? "🥈"
                          : p.rank === 3
                            ? "🥉"
                            : `#${p.rank}`}
                    </div>
                    <div
                      className="ca-lb-avatar"
                      style={{
                        background: p.color,
                        boxShadow: `0 0 10px ${p.glow}66`,
                      }}
                    >
                      {p.avatar}
                    </div>
                    <div className="ca-lb-name">
                      {p.isUser ? (
                        <>
                          <span className="ca-you-bolt">⚡</span>&nbsp;You
                        </>
                      ) : (
                        p.name
                      )}
                    </div>
                    <div className="ca-lb-score">
                      {p.score.toLocaleString()}
                      <span className="ca-lb-xp"> XP</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* XP Progress */}
            <div className="ca-card">
              <div className="ca-card-label">⚡ XP Progress</div>
              <div className="ca-xp-level-row">
                <span className="ca-xp-level">Level 12</span>
                <span className="ca-xp-next">→ Level 13</span>
              </div>
              <div className="ca-xp-bar-track">
                <div
                  className="ca-xp-bar-fill"
                  style={{ width: `${xpbarWidth}%` }}
                >
                  <span className="ca-xp-bar-label">68%</span>
                </div>
              </div>
              <div className="ca-xp-detail">
                <span className="ca-xp-current">2,180 XP earned</span>
                <span className="ca-xp-needed">3,200 XP needed</span>
              </div>
            </div>

            {/* Achievements */}
            <div className="ca-card">
              <div className="ca-card-label">🏆 Recent Achievements</div>
              <div className="ca-ach-list">
                {achievements.map((a, i) => (
                  <div className="ca-ach-row" key={i}>
                    <div
                      className="ca-ach-icon"
                      style={{
                        background: a.color + "22",
                        boxShadow: `0 0 12px ${a.color}33`,
                      }}
                    >
                      {a.icon}
                    </div>
                    <div className="ca-ach-info">
                      <div className="ca-ach-title">{a.title}</div>
                      <div className="ca-ach-desc">{a.desc}</div>
                    </div>
                    <div
                      className="ca-ach-rarity"
                      style={{ color: a.color, borderColor: a.color + "44" }}
                    >
                      {a.rarity}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Main CTA */}
            <div className="ca-cta-wrap">
              <button
                className={`ca-cta-btn ${pulseReady ? "ca-cta-btn--ready" : ""}`}
              >
                <button
                  className="ca-cta-text"
                  onClick={() => navigate("/code-arena")}
                  style={{
                    cursor: "pointer",
                    background: "none",
                    border: "none",
                    padding: 0,
                  }}
                >
                  <span className="ca-cta-icon">⚔️</span>
                  <span className="ca-cta-label">Enter Arena</span>
                  <span className="ca-cta-arrow">→</span>
                </button>
                <div className="ca-cta-particles">
                  {[...Array(8)].map((_, i) => (
                    <span
                      key={i}
                      className={`ca-particle ca-particle--${i + 1}`}
                    />
                  ))}
                </div>
              </button>
              <p className="ca-cta-hint">⚡ 247 fighters battling right now</p>
            </div>
          </div>
        </div>
      </div>
    </MyStudentLayout>
  );
}
